<script setup lang="ts">
//#region imports
import { computed, ref, watch, nextTick} from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { useApi } from '@/api';
import { useLs } from '@/setup/vueLs';
import { useTypedStore } from '@/store';
import { useFormatters } from '@/composables/useFormatters';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { usePopupManager } from '@/components/PopupManager.vue';
import _ from '@/setup/lodash';
import updateUrl from '@/service/updateUrl';
import { useTeamsList } from '@/composables/useTeamsList';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import { locales } from '@/helpers/date-locales';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type { GetMediaBuyingPostsData, GetMediaBuyingPostsResponse } from '@/types/openapi-ts';
import type { MediaBuyingPostsStatus } from '@/types/status';
//#endregion

//#region types
type MediaBuyingPostsRow = GetMediaBuyingPostsResponse['payload'][number];
type MediaBuyingPostsPermissions = NonNullable<GetMediaBuyingPostsResponse['misc']>['permissions'];
//#endregion

//#region const
const sizes = {
  1080: 20,
  1440: 30,
};

const { renderNeedAttentionIcon } = useTeamsList<MediaBuyingPostsRow>();
const fields: UiTableNewField<MediaBuyingPostsRow>[] = [
  {
    name: '',
    fixed: 'left',
    align: 'right',
    sortable: false,
    width: '30',
    minWidth: '30',
    linkAppend: renderNeedAttentionIcon,
  },
  {
    name: 'incrementing_id',
    fixed: 'left',
    align: 'left',
    sortable: 'custom',
    width: '60',
    minWidth: '60',
  },
  {
    name: 'type',
    align: 'left',
    sortable: false,
    width: '90',
    minWidth: '90',
    computedValue: computedType,
  },
  {
    name: 'affiliate_email',
    align: 'left',
    sortable: false,
    width: '180',
    minWidth: '180',
    link: getAffiliateLink,
  },
  {
    name: 'team_name',
    width: '120',
    minWidth: '120',
  },
  {
    name: 'campaign_name',
    align: 'left',
    sortable: false,
    minWidth: '180',
    flexible: true,
  },
  {
    name: 'amount',
    align: 'right',
    width: '120',
    minWidth: '120',
    computedValue: (row: MediaBuyingPostsRow) => formatCurrencyValue(row.currency_code, row.amount),
  },
  {
    name: 'amount_converted',
    align: 'right',
    width: '120',
    minWidth: '120',
    sortable: 'custom',
    computedValue: (row: MediaBuyingPostsRow) => formatCurrencyValue(currencyCode.value, row.amount_converted),
    format: 'formatMoney',
  },
  {
    name: 'period',
    align: 'left',
    width: '250',
    minWidth: '250',
    computedValue: computedPeriod,
  },
  {
    name: 'created_at',
    width: '140',
    minWidth: '140',
    align: 'left',
    headerAlign: 'left',
    sortable: 'custom',
    format: 'date-time',
  },
  {
    name: 'updated_at',
    width: '140',
    minWidth: '140',
    align: 'left',
    headerAlign: 'left',
    sortable: 'custom',
    format: 'date-time',
  },
];
//#endregion

//#region composables
const api = useApi();
const popupManager = usePopupManager();
const ls = useLs();
const store = useTypedStore();
const route = useRoute();
const { t, locale } = useI18n();
const { formatCurrencyValue } = useFormatters();
const { checkCurrencyCode } = useFormatCurrency();
const { checkRequest, checkRoute } = useDetectPermissions();

const { limit, count, page, setPage, setLsPathPage } = useTablePaginator({
  lsPath: getLsPath(),
  sizes,
  defaultSize: 15,
});

const { sortProp, sortOrder, setSort, optionsSort, setLsPath } = useTableSort({
  initialOptions:{
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: getLsPath(),
  fields,
});

const { searchQuery, setSearch } = useTableSearch();
//#endregion

//#region state
const needsAttentionFirst = ref(true);
const currentBuyingPageStatus = ref('');

const currencyCode = ref('');
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const tableData = ref<MediaBuyingPostsRow[]>([]);
const permissions = ref<MediaBuyingPostsPermissions>({});
//#endregion

//#region computed
const adminAcl = computed(() => store.getters['auth/adminAcl']);
const currentAcl = computed(() => store.getters['auth/currentAcl']);
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);
const lsPath = computed(() => getLsPath());
//#endregion

//#region methods
const _query = () => {
  return {
    ...optionsSort.value,
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    convert_amount_to_currency_code: currencyCode.value,
    needs_attention_first: needsAttentionFirst.value,
    status: currentBuyingPageStatus.value,
  } as GetMediaBuyingPostsData['query'];
};

const _completedFilterUrl = () => {
  const _dataFilters = {
    ..._.omit(_query(), ['offset', 'convert_amount_to_currency_code']),
    currency_code: currencyCode.value,
  };
  updateUrl.updateGetParams(_dataFilters);
};

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();

  try {
    const { data: { payload, misc } } = await api.getMediaBuyingPosts(options);

    count.value = misc!.count;
    permissions.value = misc!.permissions;
    currencyCode.value = misc!.currency_code;
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}


function initData() {
  currentBuyingPageStatus.value = route.meta.view as string;
  if (checkRequest('media_buying_posts_view', 'media_buying_posts_statuses_visible', currentBuyingPageStatus.value)){
    const _urlData = updateUrl.getParseParams(route.hash);

    if (!_.isEmpty(_urlData)) {
      setPage(_urlData);

      delete _urlData.page;
      delete _urlData.limit;

      _.merge(optionsSort.value, _urlData);

      setSort(_urlData);
      setSearch(_urlData);
      currencyCode.value = checkCurrencyCode(_.get(_urlData, ['currency_code']) as string);
      needsAttentionFirst.value = _.get(_urlData, ['needs_attention_first']) === 'true';
    } else {
      currencyCode.value = checkCurrencyCode(ls.get(`${lsPath.value}/currency_code`));
      needsAttentionFirst.value = ls.get(`${lsPath.value}/needs_attention_first`) || true;
    }

    nextTick(() => {
      fetchData();
    });
  }
  checkRoute('media_buying_posts_view', 'media_buying_posts_statuses_visible', currentBuyingPageStatus.value);
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

function changeCurrency(value: string) {
  currencyCode.value = value;
  ls.set(`${lsPath.value}/currency_code`, value);
  fetchData();
}

function changeAttention(value: boolean) {
  needsAttentionFirst.value = value;
  ls.set(`${lsPath.value}/needs_attention_first`, value);
  fetchData();
}

function getAffiliateLink(row: MediaBuyingPostsRow) {
  const isUser = !adminAcl.value.is_superuser && permissions.value[row.id]!.view_profile;
  const isSuperUser = adminAcl.value.is_superuser;
  return isUser || isSuperUser ? `/affiliates/${row.affiliate_id}/info` : '';
}

function formatDate(value: string) {
  return format(value, 'dd MMM yyyy', { locale: locales[locale.value as keyof typeof locales] });
}

function computedPeriod(row: MediaBuyingPostsRow) {
  return `${formatDate(row.post_period_start!)} - ${formatDate(row.post_period_end!)}`;
}

function computedType(row: MediaBuyingPostsRow) {
  return t(`mediaBuying.types.${row.type}`);
}

function calcWidth() {
  const page = currentBuyingPageStatus.value === 'on_review';
  const isUser = !adminAcl.value.is_superuser && page && currentAcl.value.media_buying_posts_review === 'allow';
  const isSuperUser = adminAcl.value.is_superuser && page;
  return isUser || isSuperUser ? 105 : 76;
}

async function markAsRead(row: MediaBuyingPostsRow) {
  if (row.needs_attention) {
    await api.postMediaBuyingPostsMarkAsRead(row.id);
  } else {
    await api.postMediaBuyingPostsMarkAsUnread(row.id);
  }
  fetchData(true);
}

function handleRowClick(row: MediaBuyingPostsRow) {
  return `/media-buying/posts/${row.id}?needs=success`;
}

function reviewActions(row: MediaBuyingPostsRow) {
  const isUser = !adminAcl.value.is_superuser && permissions.value && permissions.value[row.id]!.review_actions;
  const isSuperUser = adminAcl.value.is_superuser;
  return isSuperUser || isUser;
}

async function changeStatus(status: MediaBuyingPostsStatus, paymentId: string) {
  await api.postMediaBuyingPostsChangeStatus(paymentId, status);
  await fetchData();
};

function declineHandler(row: MediaBuyingPostsRow) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      message: t('mediaBuying.info.popup.decline_post.text'),
      title: t('mediaBuying.info.popup.decline_post.title'),
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('mediaBuying.info.popup.decline_post.button'),
      confirmType: 'negative',
      confirmIcon: 'ban',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        await changeStatus('decline', row.id);
        context.handleClose();
      },
    },
  });
}

function getLsPath() {
  return `MediaBuying/Posts/${route.meta.view}`;
}
//#endregion

//#region watch
watch(lsPath, (value) => {
  currentBuyingPageStatus.value = route.meta.view as string;

  setLsPath(value);
  setLsPathPage(value);

  searchQuery.value = '';
  currencyCode.value = checkCurrencyCode(ls.get(`${lsPath.value}/currency_code`));
  needsAttentionFirst.value = ls.get(`${lsPath.value}/needs_attention_first`) ?? true;

  nextTick(() => {
    fetchData();
  });
  popupManager.$scopeDispose();
});
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="media-buying-posts-list">
    <UiToolbar>
      <template v-slot:title>
        <div class="t:flex t:items-center t:gap-8">
          {{ $t('mediaBuying.page_title.posts.name') }}
          <UiSeparator
            type="dot"
          />
          <UiStatusText
            :type="currentBuyingPageStatus"
            :text="$t(`mediaBuying.page_title.posts.${currentBuyingPageStatus}`)"
            :font-size="20"
          />
        </div>
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
        <UiCheckbox
          v-model="needsAttentionFirst"
          :label="$t('mediaBuying.other.action_required')"
          @change="changeAttention"
        />
      </template>
      <template v-slot:right-slot>
        <UiCurrencyPicker
          :model-value="currencyCode"
          :currencies="customerCurrencies"
          @update:model-value="changeCurrency"
        />
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :currency="currencyCode"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="mediaBuying.list_table_title"
      @sort="handleSort"
    >
      <template v-slot:append>
        <ElTableColumn
          :width="calcWidth()"
          fixed="right"
          align="center"
        >
          <template v-slot:header="scope">
            <div
              :key="scope.$index"
              class="table-action-header"
            >
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              :tooltip="scope.row.needs_attention
                ? $t('mediaBuying.other.btn_unmark')
                : $t('mediaBuying.other.btn_mark')"
              :icon="scope.row.needs_attention
                ? 'circle-exclamation-check'
                : 'exclamation-circle'"
              :color="scope.row.needs_attention
                ? 'var(--text-primary)'
                : 'var(--text-negative)'"
              @click="markAsRead(scope.row)"
            />
            <ActionIcon
              icon="eye"
              :tooltip="$t('mediaBuying.actions.info')"
              :router-to="handleRowClick(scope.row)"
            />
            <ActionIcon
              v-if="currentBuyingPageStatus === 'on_review' && reviewActions(scope.row)"
              icon="times"
              color="var(--text-negative)"
              :tooltip="$t('mediaBuying.actions.decline')"
              @click="declineHandler(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>

